/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {getAssetStateLabel} from '@ion/core/src/entities/Asset';
import {AssetState} from '@ion/core/src/types/entities';
import Chip from '../components/chip/Chip';
import {MaterialExitCloseIcon} from '../icons/MaterialExitCloseIcon';
import {dlsColor} from '../theme/theme';
import {IconSize} from '../types/types';
import {resolveFilter} from './FilterHelpers';

/**
 * Props for the RenderChips component.
 *
 * @typedef {Object} RenderChipsProps
 * @property {any[]} chips - The list of chip items to render. Each item represents a selectable chip.
 * @property {Record<string, any>} optionData - Mapping of chip items to additional option data.
 * @property {Function} onChipDelete - Callback invoked when a chip is deleted or clicked.
 * @property {boolean} renderMoreItems - Whether to render items above or below the limit.
 * @property {number} numberChipsLimit - Limit for the number of chips to display when `renderIconChips` is false.
 * @property {boolean} [renderIconChips=true] - Whether to render the chips with icons.
 * @property {boolean} [additionalCondition=true] - An extra condition that controls whether to render chips.
 */
type RenderChipsProps = {
  chips: any[];
  optionData: Record<string, any>;
  onChipDelete: Function;
  renderMoreItems?: boolean;
  numberChipsLimit: number;
  renderIconChips?: boolean;
  additionalCondition?: boolean;
};

/**
 * Component to render a list of chips with options to control which chips are displayed.
 * This component supports rendering chips either with or without icons, based on conditions.
 *
 * @param {RenderChipsProps} props - The props for the component.
 */
export const RenderChips = ({
  chips,
  optionData,
  onChipDelete,
  numberChipsLimit,
  renderIconChips = true,
  renderMoreItems = false,
  additionalCondition = true
}: RenderChipsProps) => {
  /**
   * Get the data necessary to render a chip, based on the item and index.
   */
  const getChipData = (item: any, index: number): { id: string; selected: any; label?: string } | null => {
    const key = item?.selected ? resolveFilter(item?.selected) : item;
    const id = `filterSelectedOptions-${key?.value ?? item}`;
    const selected = optionData?.[item] ?? key;

    // Render chips without icons if renderIconChips is false
    if (!renderIconChips) {
      if (!renderMoreItems ? index < numberChipsLimit : index >= numberChipsLimit) {
        return {id, selected};
      }
    } else if (chips?.length) {
      const selectedLabel = selected?.title || selected?.label;
      const assetState = item?.id === 'assetStatus' ? AssetState[selectedLabel] : null;
      const label = assetState ? getAssetStateLabel(assetState)?.toUpperCase() : selectedLabel;
      return {id, selected, label};
    }

    return null;
  };

  /**
   * Renders the list of chips based on the `chips` array and conditions.
   */
  const renderChips = () => (
    chips.map((item: any, index: number) => {
      const chipData = getChipData(item, index);

      const onDelete = function() {
        onChipDelete(item);
      };

      if (chipData) {
        return (
          <Chip key={chipData.id}
            id={chipData.id}
            label={chipData.label || chipData.selected?.title || chipData.selected?.label}
            size="small"
            maxWidth={false}
            borderRadius="4px"
            labelColor={dlsColor('grey2')}
            hoverDefault
            showCustomDeleteIcon
            noTextTransform
            customDeleteIcon={<MaterialExitCloseIcon size={IconSize.small} />}
            onClick={onDelete}
            onDelete={onDelete}/>
        );
      }
      return null;
    })
  );

  return additionalCondition ? renderChips() : null;
};

export default memo(RenderChips);
